import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { Box, Skeleton } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '@/styles/components/layout/ImagesCarousel.scss';
import noPreviewImage from '@/assets/images/no-preview.png';
import {
    MAX_RETRY_COUNT,
    RetryTask,
    buildRetryUrl,
    enqueueRetryTask,
    getRetryDelay,
    hasFailedImageUrl,
    markImageUrlAsFailed,
    releaseRetrySlot,
} from '@/utils/layouts/imageRetryUtils';

function areSlidesEqual(previousSlides: string[], nextSlides: string[]) {
    if (previousSlides.length !== nextSlides.length) {
        return false;
    }

    return previousSlides.every((slide, index) => slide === nextSlides[index]);
}

export default function ImagesCarousel({ images }: { images: string[] }) {
    const slides = useMemo(() => (images.length > 0 ? images : [noPreviewImage]), [images]);
    const [loadedSlides, setLoadedSlides] = useState<Record<string, boolean>>({});
    const [slideSources, setSlideSources] = useState(slides);
    const previousSlidesRef = useRef(slides);
    const isMountedRef = useRef(true);
    const retryAttemptsRef = useRef<Record<number, number>>({});
    const retryTimersRef = useRef<Record<number, number>>({});
    const retryInFlightRef = useRef<Record<number, boolean>>({});
    const queuedRetryTaskRef = useRef<Record<number, RetryTask | null>>({});

    const releaseRetryForIndex = useCallback((index: number) => {
        if (!retryInFlightRef.current[index]) {
            return;
        }

        retryInFlightRef.current[index] = false;
        releaseRetrySlot();
    }, []);

    const setFallbackImage = useCallback((index: number, originalSrc: string) => {
        markImageUrlAsFailed(originalSrc);
        setSlideSources((prev) => {
            if (prev[index] === noPreviewImage) {
                return prev;
            }

            const nextSources = [...prev];
            nextSources[index] = noPreviewImage;
            return nextSources;
        });
    }, []);

    const clearPendingRetry = useCallback(
        (index: number, options?: { releaseInFlight?: boolean }) => {
            if (retryTimersRef.current[index]) {
                window.clearTimeout(retryTimersRef.current[index]);
                delete retryTimersRef.current[index];
            }

            const queuedTask = queuedRetryTaskRef.current[index];
            if (queuedTask) {
                queuedTask.cancelled = true;
                queuedRetryTaskRef.current[index] = null;
            }

            if (options?.releaseInFlight) {
                releaseRetryForIndex(index);
            }
        },
        [releaseRetryForIndex],
    );

    useEffect(() => {
        if (areSlidesEqual(previousSlidesRef.current, slides)) {
            return;
        }

        previousSlidesRef.current = slides;
        setLoadedSlides({});
        setSlideSources(slides);

        Object.keys(retryTimersRef.current).forEach((key) => {
            clearPendingRetry(Number(key), { releaseInFlight: true });
        });

        retryTimersRef.current = {};
        retryAttemptsRef.current = {};
        retryInFlightRef.current = {};
        queuedRetryTaskRef.current = {};
    }, [clearPendingRetry, slides]);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
            Object.keys(retryTimersRef.current).forEach((key) => {
                clearPendingRetry(Number(key), { releaseInFlight: true });
            });
        };
    }, [clearPendingRetry]);

    const handleImageLoad = useCallback(
        (index: number, slideKey: string) => {
            clearPendingRetry(index, { releaseInFlight: true });

            setLoadedSlides((prev) => {
                if (prev[slideKey]) {
                    return prev;
                }

                return {
                    ...prev,
                    [slideKey]: true,
                };
            });
        },
        [clearPendingRetry],
    );

    const handleImageError = useCallback(
        (index: number, originalSrc: string, slideKey: string) => {
            clearPendingRetry(index, { releaseInFlight: true });

            if (originalSrc === noPreviewImage) {
                setFallbackImage(index, originalSrc);
                return;
            }

            if (hasFailedImageUrl(originalSrc)) {
                setFallbackImage(index, originalSrc);
                return;
            }

            const currentAttempt = retryAttemptsRef.current[index] ?? 0;

            if (currentAttempt >= MAX_RETRY_COUNT) {
                setFallbackImage(index, originalSrc);
                return;
            }

            const nextAttempt = currentAttempt + 1;
            retryAttemptsRef.current[index] = nextAttempt;

            setLoadedSlides((prev) => {
                if (!prev[slideKey]) {
                    return prev;
                }

                const nextState = { ...prev };
                delete nextState[slideKey];
                return nextState;
            });

            const retryDelay = getRetryDelay(nextAttempt);

            retryTimersRef.current[index] = window.setTimeout(() => {
                const retryTask: RetryTask = {
                    cancelled: false,
                    run: () => {
                        queuedRetryTaskRef.current[index] = null;

                        if (!isMountedRef.current) {
                            releaseRetrySlot();
                            return;
                        }

                        retryInFlightRef.current[index] = true;
                        setSlideSources((prev) => {
                            if (prev[index] === noPreviewImage) {
                                retryInFlightRef.current[index] = false;
                                releaseRetrySlot();
                                return prev;
                            }

                            const nextSources = [...prev];
                            nextSources[index] = buildRetryUrl(originalSrc, nextAttempt);
                            return nextSources;
                        });
                    },
                };

                queuedRetryTaskRef.current[index] = retryTask;
                enqueueRetryTask(retryTask);
            }, retryDelay);
        },
        [clearPendingRetry, setFallbackImage],
    );

    return (
        <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={50}
            slidesPerView={1}
            centeredSlides={true}
            navigation
            pagination={{
                clickable: true,
                type: 'bullets',
                dynamicBullets: true,
                dynamicMainBullets: 3,
            }}
            style={{ width: '100%', height: '100%' }}
        >
            {slides.map((src, idx) => {
                const slideKey = `${src}-${idx}`;
                const isLoaded = loadedSlides[slideKey];

                return (
                    <SwiperSlide key={slideKey}>
                        <Box
                            className="images-carousel-slide"
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: '5px',
                            }}
                        >
                            {!isLoaded && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 0,
                                        background:
                                            'linear-gradient(180deg, rgba(246, 247, 249, 0.92) 0%, rgba(238, 241, 244, 0.88) 100%)',
                                        '& .MuiSkeleton-root': {
                                            borderRadius: '5px',
                                            transform: 'none',
                                            backgroundColor: 'rgba(214, 219, 226, 0.52)',
                                            '&::after': {
                                                background:
                                                    'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.42) 50%, rgba(255, 255, 255, 0) 100%)',
                                            },
                                        },
                                    }}
                                    aria-hidden="true"
                                >
                                    <Skeleton variant="rectangular" animation="wave" width="100%" height="100%" />
                                </Box>
                            )}
                            <img
                                src={slideSources[idx]}
                                loading="lazy"
                                decoding="async"
                                onLoad={() => handleImageLoad(idx, slideKey)}
                                onError={() => handleImageError(idx, src, slideKey)}
                                style={{
                                    position: 'relative',
                                    zIndex: 1,
                                    display: 'block',
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    borderRadius: '5px',
                                    opacity: isLoaded ? 1 : 0,
                                    transition: 'opacity 300ms ease',
                                }}
                            />
                        </Box>
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
}
